'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { AuditorSelector } from './AuditorSelector';
import { Button } from './Button';

interface AuditorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (auditorId: string, auditorName: string) => void;
    onUnassign?: () => void;
    currentAuditorId?: string | null;
    currentAuditorName?: string | null;
    certificationName?: string;
}

export function AuditorModal({
    isOpen,
    onClose,
    onConfirm,
    onUnassign,
    currentAuditorId,
    currentAuditorName,
    certificationName
}: AuditorModalProps) {
    const t = useTranslations('auditor');
    const [selectedAuditorId, setSelectedAuditorId] = useState<string | null>(currentAuditorId || null);
    const [selectedAuditorName, setSelectedAuditorName] = useState<string>(currentAuditorName || '');
    const [isAssigning, setIsAssigning] = useState(false);
    const [isUnassigning, setIsUnassigning] = useState(false);

    if (!isOpen) return null;

    const handleSelect = (auditorId: string, auditorName: string) => {
        setSelectedAuditorId(auditorId);
        setSelectedAuditorName(auditorName);
    };

    const handleConfirm = async () => {
        if (!selectedAuditorId || !selectedAuditorName) return;
        setIsAssigning(true);
        try {
            await onConfirm(selectedAuditorId, selectedAuditorName);
        } finally {
            setIsAssigning(false);
        }
    };

    const handleUnassign = async () => {
        if (!onUnassign) return;
        setIsUnassigning(true);
        try {
            await onUnassign();
        } finally {
            setIsUnassigning(false);
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-white/80 backdrop-blur-md rounded-3xl shadow-2xl border border-white/20 w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center text-violet-600">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-800">{t('selectAuditorTitle')}</h3>
                                {certificationName && (
                                    <p className="text-sm text-slate-500 truncate max-w-[200px]">{certificationName}</p>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-100 rounded-lg"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Current auditor info if assigned */}
                    {currentAuditorId && currentAuditorName && (
                        <div className="px-6 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-emerald-700">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm font-medium">{t('currentlyAssigned')}: {currentAuditorName}</span>
                            </div>
                            {onUnassign && (
                                <button
                                    onClick={handleUnassign}
                                    disabled={isUnassigning}
                                    className="text-xs text-rose-600 hover:text-rose-700 font-medium hover:underline disabled:opacity-50"
                                >
                                    {isUnassigning ? t('unassigning') : t('unassign')}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        <AuditorSelector
                            onSelect={handleSelect}
                            selectedAuditorId={selectedAuditorId}
                        />
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-end gap-3">
                        <Button variant="secondary" onClick={onClose}>
                            {t('cancel')}
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            disabled={!selectedAuditorId || isAssigning}
                            isLoading={isAssigning}
                            className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg shadow-violet-200 disabled:opacity-50"
                        >
                            {currentAuditorId ? t('changeAuditor') : t('selectAuditor')}
                        </Button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
